import { isEmpty } from 'lodash'
import React, { useCallback, useState } from 'react'
import { Grid, Typography, Paper, Button, makeStyles } from '@material-ui/core'

import Loading from 'components/Loading'
import BackLink from 'donation/campaigns/components/BackLink'
import usePerson from '../hooks/usePerson'
import format from 'civic-champs-shared/utils/format'
import { useForceVerify } from '../hooks/useForceVerify'
import { useChangePassword } from '../hooks/useChangePassword'
import { useChangePasswordPrompt } from '../hooks/useChangePasswordPrompt'
import { usePurgeAccountPrompt } from '../hooks/usePurgeAccountPrompt'
import { useHasRole } from 'auth/hooks'
import { INTERNAL_SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { useCurrentOrg } from '../../auth/hooks'
import { useRemoveContactPrompt } from '../hooks/useRemoveContactPrompt'
import useSetContactMethod from 'people/hooks/useSetContactMethod'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import SetContactMethodPrompt from 'people/components/SetContactMethodPrompt'
import { useGetCognitoUser } from 'people/hooks/useGetCognitoUser'
import useSetCognitoUserEnabled from 'people/hooks/useSetCognitoUserEnabled'
import { useOrganizationStatuses } from 'people/hooks/useOrganizationStatuses'
import useJumpToChampionProfile from 'people/hooks/useJumpToChampionProfile'
import { LinkButton } from 'core/table/cells'

const useStyles = makeStyles({
  linkBtn: {
    display: 'block',
    marginBlockStart: '6px',
    color: '#0F5DB5',
    textDecoration: 'none',
    cursor: 'pointer',
    textTransform: 'none',
  },
})

const useManageCognitoUser = personId => {
  const [cognitoUser, setCognitoUser] = useState(null)
  const isEnabled = !!cognitoUser?.Enabled

  const [getCognitoUser, { loading }] = useGetCognitoUser()
  const setCognitoUserEnabled = useSetCognitoUserEnabled()

  const fetch = useCallback(async () => {
    const user = await getCognitoUser(personId)
    setCognitoUser(user)
  }, [getCognitoUser, personId])

  const [modifying, setModifying] = useState(false)

  const toggleEnabled = useCallback(async () => {
    setModifying(true)
    try {
      const user = await setCognitoUserEnabled(personId, !isEnabled)
      setCognitoUser(user)
    } finally {
      setModifying(false)
    }
  }, [setCognitoUserEnabled, personId, isEnabled])

  return {
    cognitoUser,
    loading: loading || modifying,
    fetch,
    toggleEnabled,
  }
}

export const PersonDetail = props => {
  const isInternalSuperAdmin = useHasRole(INTERNAL_SUPER_ADMIN)
  const [person, setPerson] = useState(null)
  const id = props.match.params.id
  const currentOrg = useCurrentOrg()
  const { loading } = usePerson({
    id,
    onSuccess: setPerson,
    organizationId: isInternalSuperAdmin ? undefined : currentOrg.id,
  })
  const [forceVerify, { loading: verifyLoading }] = useForceVerify(setPerson)
  const [changePassword, { loading: changePasswordLoading }] = useChangePassword()
  const onChangePasswordPrompt = useChangePasswordPrompt(password => changePassword(person, password))
  const onRemoveContactPrompt = useRemoveContactPrompt(setPerson)
  const onPurgeAccountPrompt = usePurgeAccountPrompt({ personId: id, onComplete: () => props.history.push('/people') })

  const setContactMethod = useSetContactMethod()
  const showSetContactMethodPrompt = useShowPrompt(SetContactMethodPrompt)
  const onSetContactMethodPrompt = async () => {
    const { completed, value: preferredContactMethod } = await showSetContactMethodPrompt({ person })
    if (completed) {
      await setContactMethod(id, preferredContactMethod)
      setPerson(prev => ({ ...prev, preferredContactMethod }))
    }
  }

  const { cognitoUser, loading: cognitoUserLoading, fetch: showCognitoUser, toggleEnabled } = useManageCognitoUser(id)
  const { organizationStatuses } = useOrganizationStatuses(id)

  const { jumpToChampionProfile, ready: jumpReady } = useJumpToChampionProfile()

  const classes = useStyles()

  if (loading || verifyLoading || changePasswordLoading) {
    return <Loading />
  }

  if (!person) {
    return (
      <div>
        <BackLink text="Back to People" />
        <div>No person exists with this id</div>
      </div>
    )
  }
  return (
    <div>
      <BackLink text="Back to People" />
      <Grid container spacing={4} direction="column">
        <Grid container item sm={8} spacing={2}>
          <Grid item sm={12}>
            <Typography variant="h5">Person</Typography>
          </Grid>
          <Grid item sm={12}>
            <Paper variant="outlined" style={{ padding: 20 }}>
              <Grid container spacing={2}>
                <Grid item sm={3}>
                  <strong>Date</strong>
                  <p>{format.date(person.createdAt)}</p>
                </Grid>
                <Grid item sm={3}>
                  <strong>First Name</strong>
                  <p>{person.givenName}</p>
                </Grid>
                <Grid item sm={3}>
                  <strong>Last Name</strong>
                  <p>{person.familyName}</p>
                </Grid>
                <Grid item sm={3}>
                  <strong>Contact Method</strong>
                  <p>{person.preferredContactMethod}</p>
                </Grid>
                <Grid item sm={3}>
                  <strong>Account Registered</strong>
                  <p>
                    {isInternalSuperAdmin ? (
                      person.cognitoSub ? (
                        <Button onClick={showCognitoUser} disabled={cognitoUserLoading}>
                          {cognitoUser ? 'Refresh' : 'View'}
                        </Button>
                      ) : (
                        'No'
                      )
                    ) : cognitoUser ? (
                      'Yes'
                    ) : (
                      'No'
                    )}
                  </p>
                </Grid>
              </Grid>
              {isInternalSuperAdmin && (cognitoUser || cognitoUserLoading) && (
                <Grid container spacing={2}>
                  {cognitoUserLoading ? (
                    <Loading />
                  ) : (
                    <>
                      <Grid item>
                        <strong>Cognito User</strong>
                        <pre>{JSON.stringify(cognitoUser, undefined, 2)}</pre>
                        <Button onClick={toggleEnabled} disabled={cognitoUserLoading}>
                          {cognitoUser?.Enabled ? 'Disable Login' : 'Enable Login'}
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>

        {!isEmpty(organizationStatuses) && (
          <Grid container item sm={8} spacing={2}>
            <Grid item sm={12}>
              <Typography variant="h5">Organizations</Typography>
            </Grid>
            <Grid item sm={12}>
              {organizationStatuses.map(orgStatus => (
                <Paper variant="outlined" style={{ padding: 20 }} key={orgStatus.id}>
                  <Grid container spacing={2}>
                    <Grid item sm={1}>
                      <strong>ID</strong>
                      <p>{orgStatus.organizationId}</p>
                    </Grid>
                    <Grid item sm={5}>
                      <strong>Organization</strong>
                      <LinkButton
                        navigate={() => jumpToChampionProfile(orgStatus.organizationId, orgStatus.personId)}
                        disabled={!jumpReady}
                        className={classes.linkBtn}
                      >
                        {orgStatus.organizationName}
                      </LinkButton>
                    </Grid>
                    <Grid item sm={3}>
                      <strong>Status</strong>
                      <p>{orgStatus.status}</p>
                    </Grid>
                    <Grid item sm={3}>
                      <strong>Last Activity</strong>
                      <p>{format.datetime(orgStatus.lastActivityAt)}</p>
                    </Grid>
                    <Grid item sm={3}>
                      <strong>Added</strong>
                      <p>{format.datetime(orgStatus.addedAt)}</p>
                    </Grid>
                    <Grid item sm={3}>
                      <strong>Updated</strong>
                      <p>{format.datetime(orgStatus.updatedAt)}</p>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid container item sm={8} spacing={2}>
          <Grid item sm={12}>
            <Typography variant="h5">Contacts</Typography>
          </Grid>
          <Grid item sm={12}>
            {person.contacts.map(personContact => (
              <Paper variant="outlined" style={{ padding: 20 }} key={personContact.id}>
                <Grid container spacing={2}>
                  <Grid item sm={3}>
                    <strong>ID</strong>
                    <p>{personContact.id}</p>
                  </Grid>
                  <Grid item sm={3}>
                    <strong>Added</strong>
                    <p>{format.date(personContact.createdAt)}</p>
                  </Grid>
                  <Grid item sm={3}>
                    <strong>Contact</strong>
                    <p>
                      {personContact.contact.contactType === 'email'
                        ? personContact.contact.contact
                        : format.phoneNumber(personContact.contact.contact)}
                    </p>
                  </Grid>
                  <Grid item sm={3}>
                    <strong>Type</strong>
                    <p>{personContact.contactType}</p>
                  </Grid>
                  <Grid item sm={3}>
                    <strong>Status</strong>
                    <p>{personContact.status}</p>
                  </Grid>
                  {personContact.invalidatedBy && (
                    <Grid item sm={3}>
                      <strong>Invalidated By</strong>
                      <p>
                        {personContact.invalidatedBy.givenName} {personContact.invalidatedBy.familyName}
                      </p>
                    </Grid>
                  )}
                  <Grid item sm={3}>
                    <strong>Private</strong>
                    <p>{personContact.private ? 'Yes' : 'No'}</p>
                  </Grid>
                  {personContact.enteredBy && (
                    <Grid item sm={3}>
                      <strong>Entered By</strong>
                      <p>
                        {personContact.enteredBy.givenName} {personContact.enteredBy.familyName}
                      </p>
                    </Grid>
                  )}
                  <Grid item sm={3}>
                    <strong>Organization</strong>
                    <p>{personContact.organization ? personContact.organization.name : '-'}</p>
                  </Grid>
                  {isInternalSuperAdmin &&
                    person.cognitoSub &&
                    personContact.contactType === 'email' &&
                    !['verified', 'invalid'].includes(personContact.status) && (
                      <Grid item sm={3}>
                        <Button onClick={() => forceVerify({ person, email: personContact.contact.contact })}>
                          Force Verify Email
                        </Button>
                      </Grid>
                    )}
                  {isInternalSuperAdmin &&
                    person.cognitoSub &&
                    personContact.contactType === 'mobile-phone' &&
                    !['verified', 'invalid'].includes(personContact.status) && (
                      <Grid item sm={3}>
                        <Button onClick={() => forceVerify({ person, phoneNumber: personContact.contact.contact })}>
                          Force Verify Mobile Phone Number
                        </Button>
                      </Grid>
                    )}
                  {(isInternalSuperAdmin ||
                    (personContact.organization && personContact.organization.id === currentOrg.id)) &&
                    personContact.status !== 'verified' && (
                      <Grid item sm={3}>
                        <Button onClick={() => onRemoveContactPrompt(personContact)}>Remove Contact</Button>
                      </Grid>
                    )}
                </Grid>
              </Paper>
            ))}
          </Grid>
        </Grid>

        <Grid container item sm={8} spacing={2}>
          <Grid item sm={12}>
            <Typography variant="h5">Actions</Typography>
          </Grid>
          <Grid item sm={12}>
            <Paper variant="outlined" style={{ padding: 20 }}>
              <Grid container spacing={2}>
                {isInternalSuperAdmin && !person.cognitoSub && (
                  <Grid item sm={3}>
                    <Button onClick={() => props.history.push(`/people/${id}/edit`)}>Edit Name</Button>
                  </Grid>
                )}
                <Grid item sm={3}>
                  <Button onClick={() => props.history.push(`/people/${id}/add-contact`)}>Add Contact</Button>
                </Grid>
                {isInternalSuperAdmin && person.cognitoSub && (
                  <Grid item sm={3}>
                    <Button onClick={onChangePasswordPrompt}>Change Password</Button>
                  </Grid>
                )}
                {isInternalSuperAdmin && (
                  <Grid item sm={3}>
                    <Button onClick={onPurgeAccountPrompt}>Purge Account</Button>
                  </Grid>
                )}
                {isInternalSuperAdmin && (
                  <Grid item sm={3}>
                    <Button onClick={onSetContactMethodPrompt}>Set Contact Method</Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}
